import React, { useState, useEffect, memo } from "react";
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Box,
	Typography,
	Chip,
	TextField,
	Dialog,
	DialogTitle,
	DialogContent,
	IconButton,
	InputAdornment,
	TablePagination,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import ElevatedButton from "components/Button/ElevatedButton";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import SearchIcon from "@mui/icons-material/Search";
import { colors } from "components/Theme/WebTheme";

const FileView = ({ files, handleFileClick, getFileIcon }) => {
	return (
		files &&
		Object.values(files).map((file, index) => (
			<Box
				key={file.id || `file-${index}`}
				sx={{
					display: "flex",
					alignItems: "center",
					mb: 1,
					border: "1px solid #e0e0e0",
					borderRadius: "8px",
					padding: "8px",
					cursor: file.url ? "pointer" : "default",
					"&:hover": {
						backgroundColor: file.url ? "#f5f5f5" : "transparent",
					},
				}}
				onClick={() => file.url && handleFileClick(file)}
			>
				{getFileIcon(file.type)}
				<Box>
					<Typography
						variant='caption'
						display='block'
						sx={{ ml: 1 }}
					>
						{file.name}
					</Typography>
					<Typography
						variant='caption'
						color='textSecondary'
						display='block'
						sx={{
							ml: 1,
							fontSize: 12,
						}}
					>
						{file.size} MB
					</Typography>
				</Box>
			</Box>
		))
	);
};

const DocumentUserTable = ({
	documentUsers,
	onReject,
	onApprove,
	processingDocuments = new Set(),
}) => {
	const { t } = useTranslation();
	const [selectedFile, setSelectedFile] = useState(null);
	const [openDialog, setOpenDialog] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const [page, setPage] = useState(0);
	const [rowsPerPage] = useState(10);

	// Reset page to 0 when search query changes
	useEffect(() => {
		setPage(0);
	}, [searchQuery]);

	// Filter documents based on search query
	// Search across field name, user name, and user email
	const filteredDocuments = (
		Array.isArray(documentUsers) ? documentUsers : []
	).filter((doc) => {
		if (!searchQuery.trim()) {
			return true;
		}

		const query = searchQuery.toLowerCase();
		const fieldName = doc.field?.name?.toLowerCase() || "";
		const userName = doc.user_name?.toLowerCase() || "";
		const userEmail = doc.user_email?.toLowerCase() || "";

		return (
			fieldName.includes(query) ||
			userName.includes(query) ||
			userEmail.includes(query)
		);
	});

	// Handle page change
	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	// Get paginated documents
	const paginatedDocuments = filteredDocuments.slice(
		page * rowsPerPage,
		page * rowsPerPage + rowsPerPage
	);

	const getFileIcon = (type) => {
		if (type === "pdf") {
			return <PictureAsPdfIcon color='error' />;
		}
		return <ImageIcon color='primary' />;
	};

	const getFileSize = (size) => {
		return `${size} MB`;
	};

	const handleFileClick = (file) => {
		setSelectedFile(file);
		setOpenDialog(true);
	};

	const handleCloseDialog = () => {
		setOpenDialog(false);
		setSelectedFile(null);
	};

	const handleApprove = (user) => {
		const updatedField = {
			...user,
			approved: true,
			files: user.new_files,
			new_files: null,
		};
		onApprove(updatedField);
	};

	const handleReject = (user) => {
		const isRemove = user.files == null || user.files.length == 0;

		const updatedField = {
			...user,
			approved: false,
			// Don't modify existing files - only clear new_files
			files: user.files, // Keep existing approved files
			new_files: null,
		};
		onReject(updatedField, isRemove);
	};

	return (
		<Box>
			<Box sx={{ mb: 3, mt: 8 }}>
				<Typography variant='h5' component='h1'>
					{t("data_collected")}
				</Typography>
			</Box>

			{/* Add search bar below title */}
			<TextField
				fullWidth
				variant='outlined'
				placeholder={
					t("search_by_field_name") ||
					"Search by name, email, or document..."
				}
				value={searchQuery}
				onChange={(e) => setSearchQuery(e.target.value)}
				sx={{
					mb: 2,
					backgroundColor: colors.WHITE,
				}}
				InputProps={{
					startAdornment: (
						<InputAdornment position='start'>
							<SearchIcon />
						</InputAdornment>
					),
				}}
			/>

			<TableContainer component={Paper}>
				<Table>
					<TableHead>
						<TableRow sx={{ backgroundColor: colors.MAIN_COLOR }}>
							<TableCell sx={styles.alignTop}>
								{t("user")}
							</TableCell>
							<TableCell sx={styles.alignTop}>
								{t("type")}
							</TableCell>
							<TableCell sx={styles.alignTop}>
								{t("document")}
							</TableCell>
							<TableCell sx={styles.alignTop}>
								{t("information")}
							</TableCell>
							<TableCell sx={styles.alignTop}>
								{t("files")}
							</TableCell>
							<TableCell sx={styles.alignTop}>
								{t("update_files")}
							</TableCell>
							<TableCell sx={styles.alignTop}>
								{t("state")}
							</TableCell>
							<TableCell sx={styles.alignTop}>
								{t("actions")}
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{paginatedDocuments.map((user) => (
							<TableRow key={user.id}>
								<TableCell sx={styles.alignTop}>
									<Typography variant='body1'>
										{user.user_name}
									</Typography>
									<Typography
										variant='caption'
										color='textSecondary'
									>
										{user.user_email}
									</Typography>
								</TableCell>
								<TableCell sx={styles.alignTop}>
									<Chip
										label={user.user_type}
										size='small'
										color={
											user.user_type === "conductor"
												? "success"
												: "primary"
										}
									/>
								</TableCell>
								<TableCell sx={styles.alignTop}>
									{user.field.name}
								</TableCell>
								<TableCell sx={styles.alignTop}>
									{user.field.description}
								</TableCell>
								<TableCell sx={styles.alignTop}>
									<FileView
										files={user.files}
										handleFileClick={handleFileClick}
										getFileIcon={getFileIcon}
									/>
								</TableCell>
								<TableCell sx={styles.alignTop}>
									<FileView
										files={user.new_files}
										handleFileClick={handleFileClick}
										getFileIcon={getFileIcon}
									/>
								</TableCell>
								<TableCell sx={styles.alignTop}>
									<Chip
										label={
											user.approved
												? t("approved")
												: t("pending")
										}
										color={
											user.approved
												? "success"
												: "warning"
										}
										size='small'
									/>
								</TableCell>
								<TableCell sx={{ textAlign: "center" }}>
									<Box
										sx={{
											display: "flex",
											gap: 1,
											justifyContent: "center",
										}}
									>
										<ElevatedButton
											variant='contained'
											color='error'
											disabled={
												user.approved ||
												processingDocuments.has(user.id)
											}
											onClick={() => handleReject(user)}
											sx={{ fontSize: "0.875rem" }}
										>
											{processingDocuments.has(user.id)
												? t("processing") ||
												  "Processing..."
												: t("reject")}
										</ElevatedButton>
										<ElevatedButton
											variant='contained'
											color='primary'
											disabled={
												user.approved ||
												processingDocuments.has(user.id)
											}
											onClick={() => handleApprove(user)}
											sx={{ fontSize: "0.875rem" }}
										>
											{processingDocuments.has(user.id)
												? t("processing") ||
												  "Processing..."
												: t("approve")}
										</ElevatedButton>
									</Box>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
				<TablePagination
					component='div'
					count={filteredDocuments.length}
					page={page}
					onPageChange={handleChangePage}
					rowsPerPage={rowsPerPage}
					rowsPerPageOptions={[10]}
				/>
			</TableContainer>

			<Dialog
				open={openDialog}
				onClose={handleCloseDialog}
				maxWidth='md'
				fullWidth
			>
				<DialogTitle>
					<Box
						display='flex'
						alignItems='center'
						justifyContent='space-between'
					>
						<Typography variant='h6'>
							{selectedFile?.name}
						</Typography>

						<IconButton onClick={handleCloseDialog}>
							<CloseIcon />
						</IconButton>
					</Box>
				</DialogTitle>
				<DialogContent>
					<Box
						sx={{ p: 2, display: "flex", justifyContent: "center" }}
					>
						{selectedFile?.url ? (
							selectedFile.type === "pdf" ? (
								<iframe
									src={selectedFile.url}
									style={{
										width: "100%",
										height: "600px",
										border: "none",
									}}
									title={selectedFile.name}
								/>
							) : (
								<img
									src={selectedFile.url}
									alt={selectedFile.name}
									style={{
										maxWidth: "100%",
										maxHeight: "80vh",
										objectFit: "contain",
									}}
								/>
							)
						) : (
							"File preview not available"
						)}
					</Box>
				</DialogContent>
			</Dialog>
		</Box>
	);
};

export default memo(DocumentUserTable);

const styles = {
	alignTop: {
		verticalAlign: "top",
	},
};
